import { FormikValues } from "formik";
import { useQuery, useMutation } from "react-apollo";

import { useAuthContext } from "../context/AuthContext/AuthContext";
import { useNotification } from "../context/NotificationContext/NotificationContext";

import { GET_USER_ADDRESSES_QUERY } from "../../queries/userQueries";
import { UPDATE_ADDRESS_MUTATION } from "../../queries/mutations/userMutations";
import { useTranslation } from "react-i18next";
import { formMessages } from "../translation/strings";
import {
  getLoggedInDatabaseId,
  getLoggedInUserGid,
} from "../helpers/authUserIds";

const toCountryEnum = (value?: string | null) => value || null;

export const useAddressQuery = () => {
  const { t } = useTranslation();
  const authCtx = useAuthContext();
  const notificationCtx = useNotification();
  const loggedInUserGid = getLoggedInUserGid(authCtx.user);
  const loggedInCustomerId = getLoggedInDatabaseId(authCtx.user);
  const skip = !authCtx.isAuthenticated || loggedInCustomerId == null;

  const { loading, error, data } = useQuery(GET_USER_ADDRESSES_QUERY, {
    skip,
    fetchPolicy: "network-only",
    variables: {
      customerId: loggedInCustomerId,
    },
  });

  const [
    updateAddressMutation,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useMutation(UPDATE_ADDRESS_MUTATION);

  const updateAddress = async (values: FormikValues) => {
    if (!loggedInUserGid || loggedInCustomerId == null) {
      notificationCtx.danger(t(formMessages.profileNotUpdated), true);
      return;
    }

    const billingFirstName =
      values.billing?.firstName || authCtx.user?.firstName || "";
    const billingLastName =
      values.billing?.lastName || authCtx.user?.lastName || "";
    const billingCountry = toCountryEnum(values.billing?.country);
    const shippingCountry = toCountryEnum(
      values.shipping?.country || values.billing?.country
    );

    try {
      const result = await updateAddressMutation({
        variables: {
          id: loggedInUserGid,
          billingFirstName,
          billingLastName,
          billingCity: values.billing?.city,
          billingCountry,
          billingAddress: values.billing?.address1,
          billingPhone: values.billing?.phone || "",
          shippingFirstName: values.shipping?.firstName || billingFirstName,
          shippingLastName: values.shipping?.lastName || billingLastName,
          shippingCity: values.shipping?.city || values.billing?.city,
          shippingCountry,
          shippingAddress: values.shipping?.address1 || values.billing?.address1,
        },
        refetchQueries: [
          {
            query: GET_USER_ADDRESSES_QUERY,
            variables: { customerId: loggedInCustomerId },
          },
        ],
        awaitRefetchQueries: true,
      });

      if (!result?.data?.updateCustomer?.customer) {
        notificationCtx.danger(t(formMessages.profileNotUpdated), true);
        return;
      }

      notificationCtx.success(t(formMessages.profileUpdated), true);
    } catch (e) {
      notificationCtx.danger(t(formMessages.profileNotUpdated), true);
    }
  };

  return {
    loading: skip ? false : loading,
    error,
    data,
    update: {
      loading: mutationLoading,
      error: mutationError,
      data: mutationData,
      mutation: updateAddress,
    },
  };
};
